/**
 * §15's two legal clocks — the 24-hour report window and the appeal window.
 *
 * Authority: `docs/specs/control-centre/06-hearings.md` §15 (*"Two clocks with legal
 * consequence and no UI in the mockup"*), §12 (*"The `RECEIVED:` block with date and time is
 * proof of service, and the appeal clock runs from it"*), §5's panel foot and §8's late-report
 * banner (the 24-hour discretion), §2 (both thresholds are Configuration items).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DEADLINE IS DERIVED, NEVER STORED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The same principle that keeps the case stage out of the schema (§6), applied to the one
 * kind of value where it matters most. A stored appeal deadline is a number that can drift
 * from the service it was computed from: correct the service time — which migration 033
 * makes a real, logged, deliberately-supported act — and a stored deadline goes on counting
 * from an instant the record no longer claims. Deriving costs an addition; storing costs the
 * agreement between a legal deadline and the fact it is a deadline *from*.
 *
 * So there is no `appeal_deadline` column and there must not be one. There is `served_at`,
 * there is `appeal_setting.appeal_window_minutes`, and there is the function below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ABSENT CLOCK IS A STATE, NOT A BLANK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Most hearing documents, most of the time, have not been served. The wrong way to render
 * that is a dash, or worse a countdown quietly computed from the issue date — which is the
 * specific fake this module was refused in run 12 for lacking the data to avoid.
 *
 * {@link AppealWindow} therefore always reports a `basis`, and the two absent cases are
 * different facts with different remedies:
 *
 *   * `not_served` — nothing has been served, so no window has begun. The remedy is to
 *     record service once it happens.
 *   * `not_configured` — service is recorded but the tournament has no appeal window set, so
 *     the length of the window is unknown. The remedy is Configuration §05.
 *
 * Collapsing those two into "no deadline" would tell a Technical Delegate that a clock is
 * not running when in fact one is and nobody knows when it ends.
 *
 * British English throughout.
 */
import { formatDocumentDate } from "./hearing-documents.js";
/**
 * The appeal window for one document.
 *
 * `servedAt` is `hearing_document.served_at` — written only by `app_record_hearing_service()`
 * and logged every time it moves. `windowMinutes` is `appeal_setting.appeal_window_minutes`,
 * which is null unless the tournament has configured it (migration 032 refuses to default it,
 * because a guessed appeal deadline is worse than an absent one).
 *
 * **It takes the DECISION LETTER's service and no other.** §12: *"The appeal period runs from
 * service of this decision."* The Hearing Letter's service is recorded and evidences that the
 * person was told the case before it was heard (§2's second principle), and it starts nothing.
 * Which document's `served_at` reaches this function is the caller's decision, and every
 * caller in this codebase passes batch 3's.
 */
export function deriveAppealWindow(servedAt, windowMinutes) {
    if (servedAt === null) {
        return { basis: "not_served", servedAt: null, windowMinutes, deadline: null };
    }
    if (windowMinutes === null) {
        return { basis: "not_configured", servedAt, windowMinutes: null, deadline: null };
    }
    const deadline = new Date(Date.parse(servedAt) + windowMinutes * 60_000).toISOString();
    return { basis: "served", servedAt, windowMinutes, deadline };
}
/** True once the derived deadline has passed. False whenever there is no deadline at all. */
export function isAppealWindowClosed(window, now = new Date()) {
    if (window.deadline === null)
        return false;
    return now.getTime() >= Date.parse(window.deadline);
}
/**
 * Whole minutes remaining, or null when no clock is running.
 *
 * Negative values are NOT clamped to zero. A window that closed forty minutes ago is a
 * different fact from one closing now, and the screen says which — a clamped clock sitting
 * at `0m` for the rest of the tournament tells a Technical Delegate nothing about whether an
 * appeal is still live.
 */
export function appealMinutesRemaining(window, now = new Date()) {
    if (window.deadline === null)
        return null;
    return Math.round((Date.parse(window.deadline) - now.getTime()) / 60_000);
}
/**
 * §15's clock, as a sentence that states its own basis.
 *
 * The spec's requirement is a countdown; the requirement this sentence adds is that the
 * countdown says what it is counting FROM. *"Appeal window closes 20:47"* invites the reader
 * to assume the system knows something it may not. *"Served Thu 13 Aug → appeal window closes
 * Thu 13 Aug"* can be checked against the signed paper in the folder, which is the only place
 * the truth actually lives.
 */
export function describeAppealWindow(window, timezone) {
    switch (window.basis) {
        case "not_served":
            return "Not served — no appeal window has begun.";
        case "not_configured":
            return (`Served ${formatDocumentDate(window.servedAt ?? "", timezone)} — no appeal window is ` +
                "configured for this tournament, so the closing time cannot be stated.");
        case "served":
            return (`Served ${formatDocumentDate(window.servedAt ?? "", timezone)} → appeal window ` +
                `closes ${formatDocumentDate(window.deadline ?? "", timezone)}`);
    }
}
/**
 * §15's first clock. *"Build a countdown chip per case from the incident time, and mark the
 * case Late automatically so the seventh natural-justice item is forced on."*
 *
 * Both halves already existed in substance before this file: `hearing_case.is_late_report` is
 * computed at creation, and §10's letter prints the late-report paragraph and its reasons.
 * What was missing was the DEADLINE as a value a screen can count towards, which is a
 * subtraction away — so it is here rather than declared as a gap.
 *
 * The threshold is `appeal_setting.late_report_threshold_hours` (migration 002), which is 24
 * for this tournament and is configuration rather than a constant, because §2 lists it as a
 * Configuration item and a tournament may run a different one.
 */
export function deriveLateReportWindow(incidentAt, reportReceivedAt, thresholdHours, now = new Date()) {
    const deadlineMs = Date.parse(incidentAt) + thresholdHours * 3_600_000;
    const measuredAt = reportReceivedAt === null ? now.getTime() : Date.parse(reportReceivedAt);
    return {
        deadline: new Date(deadlineMs).toISOString(),
        // Measured against the REPORT, not against the clock: a case whose report arrived in time
        // does not become late by being looked at two days later.
        isLate: measuredAt > deadlineMs,
        hoursRemaining: Math.round((deadlineMs - now.getTime()) / 3_600_000),
        thresholdHours,
    };
}
//# sourceMappingURL=hearing-clocks.js.map